import {
  CircleNotchIcon,
  FileArrowDownIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useApiMutation } from '@/modules/common';

import { InventoryReportType } from '../../../../constants';

interface InventoryActionsProps {
  eventId: string;
  stationId: string;
  isGeneratingReport: boolean;
  onGenerateReport: (reportType: InventoryReportType) => Promise<void>;
}

export function InventoryActions({
  eventId,
  stationId,
  onGenerateReport,
  isGeneratingReport,
}: InventoryActionsProps) {
  const queryClient = useQueryClient();
  const [isRemoveInventoryDialogOpen, setIsRemoveInventoryDialogOpen] =
    useState(false);
  const [activeReportType, setActiveReportType] =
    useState<InventoryReportType | null>(null);

  const openRemoveInventoryDialog = async () => {
    setIsRemoveInventoryDialogOpen(true);
  };

  const removeEventProductsMutation = useApiMutation<void>(
    `/events/${eventId}/stations/${stationId}/products`,
    {
      method: 'DELETE',
    },
    {
      onError: () => {
        toast.error('Uh oh! Something went wrong.');
      },
    }
  );

  const removeEventProducts = async () => {
    try {
      await removeEventProductsMutation.mutateAsync();
      await queryClient.invalidateQueries({
        queryKey: ['eventProducts', eventId, stationId],
      });
      toast.success('Inventory removed successfully');
    } catch (error) {
      console.error(error.message);
    }
  };

  const downloadReport = async (reportType: InventoryReportType) => {
    setActiveReportType(reportType);
    await onGenerateReport(reportType);
  };

  return (
    <div className="flex flex-wrap gap-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            key="download-checked-out-bags-report"
            data-testid="download-checked-out-bags-report-button"
            variant="outline"
            startIcon={<FileArrowDownIcon />}
            onClick={() => downloadReport(InventoryReportType.CheckedOutBags)}
            isLoading={
              isGeneratingReport &&
              activeReportType === InventoryReportType.CheckedOutBags
            }
          >
            Download Bags
          </Button>
        </TooltipTrigger>
        <TooltipContent>Download Checked-Out Bags Report</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            key="download-back-stock-inventory-report"
            data-testid="download-back-stock-inventory-report-button"
            variant="outline"
            startIcon={<FileArrowDownIcon />}
            onClick={() =>
              downloadReport(InventoryReportType.BackStockInventory)
            }
            isLoading={
              isGeneratingReport &&
              activeReportType === InventoryReportType.BackStockInventory
            }
          >
            Download Back-Stock
          </Button>
        </TooltipTrigger>
        <TooltipContent>Download Back Stock Inventory Report</TooltipContent>
      </Tooltip>

      <Button
        key="remove-inventory"
        data-testid="remove-inventory-button"
        variant="outline"
        startIcon={<TrashIcon />}
        onClick={openRemoveInventoryDialog}
        className="text-destructive border-destructive hover:text-destructive bg-transparent"
        isLoading={removeEventProductsMutation.isPending}
      >
        Remove Inventory
      </Button>

      <AlertDialog
        open={isRemoveInventoryDialogOpen}
        onOpenChange={setIsRemoveInventoryDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Are you sure you want to delete all inventory for this event?
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeEventProductsMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={removeEventProductsMutation.isPending}
              onClick={removeEventProducts}
              className="text-destructive border-destructive hover:text-destructive w-[150px]"
              data-testid="remove-inventory-dialog-button"
            >
              {removeEventProductsMutation.isPending && (
                <CircleNotchIcon className="animate-spin" />
              )}
              Remove Inventory
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
